import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { isPlatformOrGlobalGm } from '../lib/roleHelpers'
import { ArrowLeft, Users, Plus, ChevronRight, RefreshCw, X, Check } from 'lucide-react'

const A = {
  page: { minHeight: '100vh', background: 'radial-gradient(ellipse at 50% 10%, rgba(77,214,255,.08), transparent 60%), #0e1116', color: '#eaf2fb', fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif" },
  nav: { background: 'linear-gradient(135deg, #2a2e36, #1a1d22)', borderBottom: '1px solid #4a5058', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  display: { fontFamily: "'Orbitron', 'Rajdhani', sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase' },
  muted: { color: '#9aa6b0' },
  faded: { color: '#5a6470' },
  panel: { background: 'linear-gradient(135deg, #2a2e36, #1a1d22)', border: '1px solid #4a5058', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06), inset 0 -1px 0 rgba(0,0,0,.4), 0 4px 12px rgba(0,0,0,.4)', position: 'relative' },
  input: { width: '100%', background: 'rgba(77,214,255,.04)', color: '#eaf2fb', border: '1px solid rgba(77,214,255,.25)', padding: '10px 14px', fontFamily: "'Saira Condensed', sans-serif", fontSize: '14px', outline: 'none' },
  btnPrimary: { fontFamily: "'Orbitron', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff7a18, #d8500a)', color: '#fff5e8', border: 'none', padding: '8px 16px', cursor: 'pointer', boxShadow: '0 0 14px rgba(255,122,24,.5), inset 0 1px 0 rgba(255,255,255,.25)', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  btnSecondary: { fontFamily: "'Saira Condensed', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'rgba(77,214,255,.06)', color: '#4dd6ff', border: '1px solid rgba(77,214,255,.25)', padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  btnNav: { color: '#5a6470', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: "'Saira Condensed', sans-serif", fontSize: '14px', letterSpacing: '0.08em', textTransform: 'uppercase' },
  chip: { fontFamily: "'Orbitron', sans-serif", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '2px 8px' },
  chipLead: { background: 'rgba(255,122,24,.12)', color: '#ff7a18', border: '1px solid rgba(255,122,24,.3)' },
  chipMember: { background: 'rgba(77,214,255,.08)', color: '#4dd6ff', border: '1px solid rgba(77,214,255,.25)' },
}

export default function Teams() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [myTeams, setMyTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDesc, setNewTeamDesc] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => { fetchMyTeams() }, [user])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('create') === 'true') {
      setShowCreate(true)
      navigate('/teams', { replace: true })
    }
  }, [navigate])

  const fetchMyTeams = async () => {
    if (!user) return; setLoading(true)
    // Two-step: pull memberships, then enrich with v_team_aggregate_stats so
    // the row card shows real points/kills/events from the stats view rather
    // than the deprecated team_stats table.
    const { data: memberships } = await supabase
      .from('team_members')
      .select('team_role, teams(*)')
      .eq('user_id', user.id)
      .order('joined_at', { ascending: false })
    const teamIds = (memberships || []).map(m => m.teams?.id).filter(Boolean)
    let aggMap = {}
    if (teamIds.length > 0) {
      const { data: agg } = await supabase
        .from('v_team_aggregate_stats')
        .select('team_id, team_total_points, team_kills_total, team_kills_confirmed, team_deaths, events_played, kd_ratio')
        .in('team_id', teamIds)
      ;(agg || []).forEach(a => { aggMap[a.team_id] = a })
    }
    const enriched = (memberships || []).map(m => ({
      ...m,
      team_aggregate: m.teams ? (aggMap[m.teams.id] || null) : null,
    }))
    setMyTeams(enriched); setLoading(false)
  }

  const createTeam = async () => {
    const { validateCreateTeam } = await import('../lib/validate')
    const errors = validateCreateTeam({ name: newTeamName, description: newTeamDesc })
    if (Object.keys(errors).length > 0) { setMessage(Object.values(errors)[0]); return }
    setCreating(true)
    const handle = newTeamName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    const { data: team, error } = await supabase.from('teams').insert({ name: newTeamName.trim(), handle, description: newTeamDesc.trim() || null, created_by: user.id }).select().limit(1)
    if (error || !team?.[0]) {
      const msg = error?.code === '23505'
        ? 'A team with that name is already taken — try a different name'
        : 'Failed: ' + (error?.message || 'unknown')
      setMessage(msg); setCreating(false); return
    }
    const { error: memberError } = await supabase.from('team_members').insert({ team_id: team[0].id, user_id: user.id, team_role: 'lead' })
    if (memberError) { setMessage('Team created but failed to add as lead: ' + memberError.message) }
    else { setMessage('Team created'); setNewTeamName(''); setNewTeamDesc(''); setShowCreate(false); fetchMyTeams() }
    setCreating(false)
  }

  // Pull aggregate stats off the enriched membership row. v_team_aggregate_stats
  // returns null for teams with no logged events — guard so the card still
  // renders zero-state numbers.
  const aggregateFor = (entry) => entry.team_aggregate || {}

  return (
    <div style={A.page}>
      <nav style={A.nav}>
        <div className="flex items-center gap-4">
          {/* Back nav: history-based when there's a real prior route, fall
              back to dashboard for platform/GMs and /play for players. The
              prior hardcoded `/dashboard` jump pulled players into the GM
              dashboard from any Teams entry point (Bug 23). */}
          <button onClick={() => {
            if (window.history.length > 1) { navigate(-1); return }
            window.location.href = isPlatformOrGlobalGm(profile) ? '/dashboard' : '/play'
          }} style={A.btnNav}><ArrowLeft size={14} /> Back</button>
          <span style={{ ...A.display, fontSize: '15px', color: '#ff7a18' }}>◆ TEAMS</span>
        </div>
        <button onClick={fetchMyTeams} style={{ ...A.btnNav, color: '#5a6470' }}><RefreshCw size={14} /></button>
      </nav>

      <main className="max-w-2xl mx-auto p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold" style={A.display}>MY TEAMS</h2>
            <p className="text-sm mt-1" style={A.muted}>Select a team to open Command Center</p>
          </div>
          <button onClick={() => setShowCreate(!showCreate)} style={A.btnPrimary}><Plus size={14} /> NEW TEAM</button>
        </div>

        {showCreate && (
          <div style={{ ...A.panel, padding: '16px', marginBottom: '16px', marginTop: '16px' }}>
            <p className="text-sm font-medium mb-3" style={{ ...A.display, fontSize: '12px' }}>CREATE TEAM</p>
            <div className="space-y-2">
              <input type="text" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="Team name" maxLength={48} style={A.input} />
              <input type="text" value={newTeamDesc} onChange={(e) => setNewTeamDesc(e.target.value)} placeholder="Description (optional)" maxLength={120} style={A.input} />
              <div className="flex gap-2 pt-1">
                <button onClick={createTeam} disabled={creating || !newTeamName.trim()} style={{ ...A.btnPrimary, opacity: creating ? 0.5 : 1 }}><Check size={14} /> CREATE</button>
                <button onClick={() => { setShowCreate(false); setNewTeamName(''); setNewTeamDesc('') }} style={A.btnSecondary}><X size={14} /> CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className="mb-4 p-3 text-sm" style={{ ...A.panel, padding: '12px', color: message.includes('Failed') ? '#ff4136' : '#4dd6ff', borderColor: message.includes('Failed') ? 'rgba(255,65,54,.3)' : 'rgba(77,214,255,.3)' }}>
            {message}
          </div>
        )}

        {loading ? (
          <p className="mt-8" style={A.muted}>Loading teams...</p>
        ) : myTeams.length === 0 ? (
          <div className="text-center py-16">
            <Users size={40} className="mx-auto mb-3" style={{ color: '#5a6470', opacity: 0.5 }} />
            <p style={A.faded}>You're not on any teams yet.</p>
            <p className="text-sm mt-1" style={A.faded}>Create one or ask a team lead to add you.</p>
          </div>
        ) : (
          <div className="space-y-2 mt-6">
            <p className="text-sm" style={A.faded}>{myTeams.length} team{myTeams.length !== 1 ? 's' : ''}</p>
            {myTeams.map(entry => {
              const team = entry.teams; if (!team) return null
              const myRole = entry.team_role
              const teamStats = aggregateFor(entry)
              return (
                <button key={team.id} onClick={() => navigate(`/teams/${team.id}`)}
                  className="w-full flex items-center justify-between text-left transition-all p-4"
                  style={{ ...A.panel, cursor: 'pointer' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold text-lg"
                      style={{ background: 'linear-gradient(135deg, #ff7a18, #d8500a)', color: '#fff5e8', fontFamily: "'Orbitron', sans-serif" }}>
                      {team.name[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium" style={{ ...A.display, fontSize: '14px' }}>{team.name}</p>
                        <span style={{ ...A.chip, ...(myRole === 'lead' ? A.chipLead : A.chipMember) }}>
                          {myRole === 'lead' ? 'LEAD' : 'MEMBER'}
                        </span>
                      </div>
                      {team.description && <p className="text-xs mt-0.5" style={A.muted}>{team.description}</p>}
                      <p className="text-xs mt-0.5" style={A.faded}>
                        {teamStats.team_total_points || 0} pts · {teamStats.events_played || 0} events · {teamStats.team_kills_total || 0} kills
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={16} style={A.faded} />
                </button>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
